import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useLazyGetSubClientsQuery} from '../../../../../services/cellcast.v2'
import {GetSubClientsParam} from '../../../../../services/cellcast.v2.types'
import InsertNewData from '../../../../Components/InsertNewData'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import moment from 'moment-timezone'
import ListFilter from './ListFilter'
import ListAction from './ListActions'
import WriteUser, {WriteUserMode} from '../Modal/WriteUser'
import {Link} from 'react-router-dom'
import {useLoginAsOwnerUser} from '../Hooks/useLoginAsOwnerUser'
import {useDeleteOwnerUser} from '../Hooks/useDeleteOwnerUser'
import {FixedTwoDecimal} from '../../../../Utils/_helpers/string'

const OwnerList: FC = () => {
  const [fetchData, {data, isLoading, isFetching}] = useLazyGetSubClientsQuery()
  const handleLoginAsUser = useLoginAsOwnerUser()

  // local state
  const [params, setParams] = useState<GetSubClientsParam>({
    page: 1,
    limit: 10,
  })
  const [reload, setReload] = useState<boolean>(false)
  const [loadedData, setLoadedData] = useState<{
    startLoad: number
    endLoad: number
  }>({
    startLoad: 0,
    endLoad: 0,
  })

  const {setDeleteId, setShowDeleteModal, DeleteModal} = useDeleteOwnerUser(() => {
    setReload(true)
  })

  // local state - toggle modal
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  const getLoadedData = useCallback(
    (
      page: string | number | undefined,
      limit: string | number | undefined,
      totalData: number
    ): {
      startLoad: number
      endLoad: number
    } => {
      let loadedPage = 1
      if (typeof page === 'string') {
        loadedPage = parseInt(page) || 1
      }

      if (typeof page === 'number') {
        loadedPage = page || 1
      }

      let loadedLimit = 10
      if (typeof limit === 'string') {
        loadedLimit = parseInt(limit) || 1
      }

      if (typeof limit === 'number') {
        loadedLimit = limit || 10
      }

      const startItem = (loadedPage - 1) * loadedLimit + 1
      const endItem = Math.min(loadedPage * loadedLimit, totalData)

      return {
        startLoad: startItem,
        endLoad: endItem,
      }
    },
    []
  )

  // first init
  useEffect(() => {
    fetchData(params)
  }, [])

  // first init
  useEffect(() => {
    if (reload) {
      fetchData(params)
      setReload(false)
    }
  }, [reload, params])

  // listed fetch data
  useEffect(() => {
    if (data) {
      const loadedData = getLoadedData(
        data && data.data.current ? data.data.current : 1,
        params.limit,
        data && data.data.total ? data.data.total : 0
      )
      setLoadedData(loadedData)
    }
  }, [isLoading, isFetching])

  // Modal
  const CreateModal = useMemo(
    () => (
      <WriteUser
        show={showCreateModal}
        handleClose={() => {
          setShowCreateModal(false)
        }}
        mode={WriteUserMode.ADD}
        callbackFn={() => {
          setParams({...params, page: 1, industry: '', search: '', status: ''})
          setShowCreateModal(false)
          setReload(true)
        }}
      />
    ),
    [params, showCreateModal]
  )

  return (
    <>
      {CreateModal}
      {DeleteModal}
      <div className='row'>
        <div className='mt-8'>
          <div className='card p-8'>
            <div className='card-header border-0 p-0 align-items-center mb-10'>
              <div className='d-flex flex-column w-100'>
                <h3 className='card-title align-items-start  flex-column'>
                  <span className='card-label fw-bolder fs-2 mb-1'>All Sub Client Accounts</span>
                  <span className='text-muted mt-1 fw-bold fs-7'>Ordered by most recent</span>
                </h3>
              </div>
              <ListFilter params={params} setParams={setParams} doFetch={() => setReload(true)} />
              <ListAction showModalCreate={() => setShowCreateModal(true)} />
            </div>
            {isLoading ? (
              <div className='row flex-center h-200'>
                <Spinner />
              </div>
            ) : (
              <>
                {data?.data?.items && data.data.items.length > 0 ? (
                  <div className='card-body p-0 pt-1'>
                    <div className='table-responsive'>
                      <table className='table align-middle gs-0 gy-5 all-message-table'>
                        <thead>
                          <tr className='fw-400 text-muted top-align'>
                            {/* <th className='w-25px'>
                              <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  value='ALL'
                                  data-kt-check='true'
                                  data-kt-check-target='.widget-9-check'
                                />
                              </div>
                            </th> */}
                            <th className='min-w-130px top-align'>USER</th>
                            <th className='top-align'>COMPANY</th>
                            <th className='top-align'>EMAIL</th>
                            <th className='top-align text-center'>
                              SHARING <br />
                              CREDIT
                            </th>
                            <th className='min-w-100px top-align'>ALLOCATED CREDIT</th>
                            <th className='min-w-100px top-align'>USED CREDIT</th>
                            <th className='top-align'>STATUS</th>
                            <th className='top-align'>CREATED DATE</th>
                            <th className='min-w-150px text-end'>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isFetching ? (
                            <tr>
                              <td colSpan={8} className='text-center'>
                                <Spinner />
                              </td>
                            </tr>
                          ) : (
                            <>
                              {data?.data?.items?.map((data, i: number) => (
                                <tr key={i}>
                                  {/* <td>
                                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                      <input
                                        className='form-check-input widget-9-check'
                                        type='checkbox'
                                        value={data?._id}
                                        checked={selectedData?.includes(data?._id)}
                                        // onChange={handleCheck}
                                      />
                                    </div>
                                  </td> */}
                                  <td className='top-align'>
                                    <Link to={'/subAccount/user/' + data._id}>{data.name}</Link>
                                  </td>
                                  <td className='top-align'>{data.companyName || '-'}</td>
                                  <td className='top-align'>{data.email || '-'}</td>
                                  <td className='top-align text-center'>
                                    {data.isSharingCredit ? 'yes' : 'no'}
                                  </td>
                                  <td className='top-align'>
                                    <span>SMS : {FixedTwoDecimal(data.credit.allocatedSms)}</span>
                                    <br />
                                    <span>MMS : {FixedTwoDecimal(data.credit.allocatedMms)}</span>
                                  </td>
                                  <td className='top-align'>
                                    <span>SMS : {FixedTwoDecimal(data.credit.usedSms)}</span>
                                    <br />
                                    <span>MMS : {FixedTwoDecimal(data.credit.usedMms)}</span>
                                    <br />
                                  </td>
                                  <td className='top-align'>{data.status2}</td>
                                  <td className='top-align'>
                                    {' '}
                                    {moment(data?.createdAt).format('DD-MM-YYYY HH:mm:ss')}
                                  </td>
                                  <td className='top-align'>
                                    <div className='row'>
                                      <OverlayTrigger
                                        placement='bottom'
                                        delay={{show: 250, hide: 400}}
                                        overlay={<Tooltip id='tooltip-bottom'>Detail</Tooltip>}
                                      >
                                        <Link
                                          to={'/subAccount/user/' + data._id}
                                          className='ms-2 col-6 btn btn-icon btn-success btn-sm me-1 mb-3'
                                        >
                                          <span className='svg-icon svg-icon-3'>
                                            <i className='bi bi-eye'></i>
                                          </span>
                                        </Link>
                                      </OverlayTrigger>
                                      <OverlayTrigger
                                        placement='bottom'
                                        delay={{show: 250, hide: 400}}
                                        overlay={<Tooltip id='tooltip-bottom'>Delete</Tooltip>}
                                      >
                                        <button
                                          className='ms-2 col-6 btn btn-icon btn-danger btn-sm me-1 mb-3'
                                          onClick={(e) => {
                                            setDeleteId(data._id)
                                            setShowDeleteModal(true)
                                          }}
                                        >
                                          <span className='svg-icon svg-icon-3'>
                                            <i className='bi bi-trash'></i>
                                          </span>
                                        </button>
                                      </OverlayTrigger>
                                      <OverlayTrigger
                                        placement='bottom'
                                        delay={{show: 250, hide: 400}}
                                        overlay={
                                          <Tooltip id='tooltip-bottom'>Login As User</Tooltip>
                                        }
                                      >
                                        <button
                                          className='ms-2 col-6 btn btn-icon btn-primary btn-sm me-1 mb-3'
                                          onClick={(e) => handleLoginAsUser(data._id, false)}
                                        >
                                          <span className='svg-icon svg-icon-3'>
                                            <i className='bi bi-key'></i>
                                          </span>
                                        </button>
                                      </OverlayTrigger>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className='abcd'>
                      <select
                        onChange={(e: any) => {
                          setParams({...params, limit: e.target.value})
                          setReload(true)
                        }}
                      >
                        <option value={10} selected={params.limit === 10}>
                          10
                        </option>
                        <option value={20} selected={params.limit === 20}>
                          20
                        </option>
                        <option value={50} selected={params.limit === 50}>
                          50
                        </option>
                      </select>
                      {isFetching
                        ? '. . . . .'
                        : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                            data.data.total ? data.data.total : 0
                          } data`}
                      <ReactPaginate
                        previousLabel='<'
                        nextLabel='>'
                        pageCount={data?.data?.totalPages}
                        onPageChange={(e: any) => {
                          setParams({...params, page: e.selected + 1})
                          setReload(true)
                        }}
                        containerClassName={'pagination'}
                        previousLinkClassName={'pagination__link'}
                        nextLinkClassName={'pagination__link'}
                        disabledClassName={'pagination__link--disabled'}
                        activeClassName={'pagination__link--active'}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    {isFetching ? (
                      <div className='row flex-center h-200'>
                        <Spinner />
                      </div>
                    ) : (
                      <InsertNewData
                        message={{
                          title: `You don't have any owner users yet`,
                        }}
                        isButtonShowing={false}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default OwnerList
